import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../services/apiUtils';
import { DrawerSection, EmptyState, StatusBadge } from './ui';
import { leadTypeLabel } from './leadAdapters';

type Candidate = {
  candidateId: string;
  proposedDisplayName: string | null;
  identifiers: { emails: string[]; phones: string[] };
  sourceCount: number;
  enquiryCount: number;
  sources: string[];
  firstSeen: string | null;
  lastSeen: string | null;
  associatedAssociates: Array<{ name: string | null; kwuid: string | null; marketCentre: string | null; enquiryCount: number }>;
  listingCount: number;
  dnc: { hasHistory: boolean; status: string; scopes: string[]; channels: string[]; associateScopedKwuids: string[] };
  matchClassification: string;
  previousClassification: string;
  classificationChanged: boolean;
  downgradeReasons: string[];
  matchReasons: string[];
  conflictReasons: string[];
  observedNames: Array<{ name: string; frequency: number }>;
  matchGraph: string[];
  risk: string;
  riskReasons: string[];
  enquiries: Array<{ id: string; source: string; sourceEventId: string; receivedAt: string | null; occurredAt: string | null; leadType: string | null; listingReference: string | null; associateName: string | null; associateKwuid: string | null; marketCentre: string | null; message: string | null }>;
};

export type IdentityDecision = { candidateId: string; decision: string; reviewedAt: string; reviewedBy: string; reasonCodes: string[]; notes: string; excludedIdentifiers: string[]; splitGroups: Array<{ group: string; eventIds: string[] }> };

type AnalysisResponse = {
  summary: Record<string, number | Record<string, number>>;
  candidates: Candidate[];
  review: Candidate[];
  autoSample: Candidate[];
  riskReport: { totalAutoMatch: number; riskCounts: Record<string, number>; reasonCounts: Record<string, number>; genericSharedEmailCandidates: number };
  generatedAt: string;
  decisions: Record<string, IdentityDecision>;
  decisionProjection: Record<string, number>;
};

type QualitySummary = {
  totalEvents: number;
  byClassification: Record<string, number>;
  bySource: Record<string, Record<string, number>>;
  exactDuplicates: number;
  nearDuplicates: number;
  repeatedInterestPreserved: number;
  repeatedMessages: number;
  portalTemplateMessages: number;
  portalTemplateEvents: number;
  associateIdentifierEvents: number;
  genericEmailEvents: number;
  exampleTestDomainEvents: number;
  highVolumeOver5PerDay: number;
  highVolumeOver10PerDay: number;
  highVolumeOver20PerDay: number;
  highVolumeNormalEvents: number;
  suspiciousHighVolumeEvents: number;
  mediumRiskCandidates: number;
  mediumRiskNormalOnly: number;
  mediumRiskWithDuplicates: number;
  mediumRiskWithInternalTest: number;
  mediumRiskWithSpamOrMalformed: number;
  reviewCandidates: number;
  reviewNormalOnly: number;
  reviewWithDuplicates: number;
  reviewWithInternalTest: number;
  reviewWithSpamOrMalformed: number;
};

// Older or partial API responses may omit summary fields.
type QualityApiResponse = { summary?: Partial<QualitySummary> };

const QUALITY_SUMMARY_DEFAULTS: QualitySummary = {
  totalEvents: 0, byClassification: {}, bySource: {}, exactDuplicates: 0, nearDuplicates: 0, repeatedInterestPreserved: 0, repeatedMessages: 0,
  portalTemplateMessages: 0, portalTemplateEvents: 0, associateIdentifierEvents: 0, genericEmailEvents: 0, exampleTestDomainEvents: 0,
  highVolumeOver5PerDay: 0, highVolumeOver10PerDay: 0, highVolumeOver20PerDay: 0, highVolumeNormalEvents: 0, suspiciousHighVolumeEvents: 0,
  mediumRiskCandidates: 0, mediumRiskNormalOnly: 0, mediumRiskWithDuplicates: 0, mediumRiskWithInternalTest: 0, mediumRiskWithSpamOrMalformed: 0,
  reviewCandidates: 0, reviewNormalOnly: 0, reviewWithDuplicates: 0, reviewWithInternalTest: 0, reviewWithSpamOrMalformed: 0,
};

export function mergeQualitySummary(summary: Partial<QualitySummary>): QualitySummary {
  return { ...QUALITY_SUMMARY_DEFAULTS, ...summary, byClassification: summary.byClassification ?? {}, bySource: summary.bySource ?? {} };
}

type SourceStatusRecord = { state?: string; total?: number; lastLeadAt?: string | null; enabled?: boolean; canonicalIngestionEnabled?: boolean };

export const ANALYSIS_VIEWS = [
  { id: 'identity', label: 'Identity Analysis' },
  { id: 'quality', label: 'Lead Quality' },
  { id: 'sources', label: 'Source Health' },
  { id: 'readiness', label: 'Canonical Readiness' },
] as const;
type AnalysisView = (typeof ANALYSIS_VIEWS)[number]['id'];

const SOURCES = [
  { id: 'PROPERTY24', label: 'Property24', statusKey: 'property24', color: '#0f766e' },
  { id: 'PRIVATE_PROPERTY', label: 'Private Property', statusKey: 'privateProperty', color: '#b45309' },
  { id: 'KWHOMES', label: 'KWHomes', statusKey: 'kwhomes', color: '#be123c' },
] as const;

const QUALITY_CLASSES: Array<{ id: string; label: string; color: string }> = [
  { id: 'NORMAL', label: 'Normal', color: '#059669' },
  { id: 'PORTAL_TEMPLATE_NORMAL', label: 'Portal-template normal', color: '#6ee7b7' },
  { id: 'LIKELY_DUPLICATE', label: 'Duplicate', color: '#f59e0b' },
  { id: 'INTERNAL_TEST', label: 'Internal / test', color: '#64748b' },
  { id: 'POSSIBLE_SPAM', label: 'Possible spam', color: '#e11d48' },
  { id: 'MALFORMED', label: 'Malformed', color: '#881337' },
  { id: 'REVIEW_REQUIRED', label: 'Needs review', color: '#0ea5e9' },
];

const REVIEW_REASONS = ['IDENTIFIER_NAME_CONFLICT', 'SHARED_EMAIL', 'SHARED_PHONE', 'AMBIGUOUS_BRIDGE', 'GENERIC_SHARED_EMAIL'];

const DECISION_OPTIONS: Array<[string, string]> = [
  ['APPROVE_MERGE', 'Confirm same person'],
  ['SPLIT', 'Split into separate people'],
  ['EXCLUDE_IDENTIFIER', 'Exclude shared identifier'],
  ['KEEP_REVIEW_REQUIRED', 'Keep for later review'],
  ['APPROVE_AUTO_MATCH', 'Confirm auto-match'],
  ['REJECT_AUTO_MATCH', 'Reject auto-match'],
  ['NEEDS_INVESTIGATION', 'Needs investigation'],
];

const QUEUE_PAGE = 25;

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function formatShare(part: number, total: number): string {
  return total > 0 ? `${((part / total) * 100).toFixed(1)}%` : '—';
}

function humanize(value: string): string {
  const text = value.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function decisionLabel(value: string | undefined): string {
  return DECISION_OPTIONS.find(([id]) => id === value)?.[1] ?? 'Not reviewed';
}

function dateLabel(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function statusTone(value: string): 'good' | 'warning' | 'danger' | 'info' {
  if (value === 'AUTO_MATCH' || value === 'CLEAR' || value === 'LOW') return 'good';
  if (value === 'DO_NOT_CONTACT' || value === 'HIGH') return 'danger';
  if (value === 'REVIEW_REQUIRED' || value === 'MEDIUM') return 'warning';
  return 'info';
}

export function summarizeReviewProgress(reviewIds: string[], decisions: Record<string, unknown>, mediumRiskTotal: number): { review: { reviewed: number; remaining: number; total: number }; mediumRisk: { reviewed: number; remaining: number; total: number } } {
  const reviewSet = new Set(reviewIds);
  const decided = Object.keys(decisions);
  const reviewReviewed = decided.filter((id) => reviewSet.has(id)).length;
  const autoReviewed = Math.min(mediumRiskTotal, decided.length - reviewReviewed);
  return {
    review: { reviewed: reviewReviewed, remaining: Math.max(0, reviewIds.length - reviewReviewed), total: reviewIds.length },
    mediumRisk: { reviewed: autoReviewed, remaining: Math.max(0, mediumRiskTotal - autoReviewed), total: mediumRiskTotal },
  };
}

function Panel({ title, description, aside, children, className = '' }: { title: string; description?: string; aside?: ReactNode; children: ReactNode; className?: string }): JSX.Element {
  return (
    <section className={`surface-card p-5 ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {description ? <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p> : null}
        </div>
        {aside}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }): JSX.Element {
  return (
    <div className="min-w-0 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
      {hint ? <div className="mt-0.5 truncate text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function StatStrip({ children }: { children: ReactNode }): JSX.Element {
  return <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 lg:grid-cols-4">{children}</div>;
}

function BarRow({ label, value, total, color = '#0f172a' }: { label: string; value: number; total: number; color?: string }): JSX.Element {
  const width = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 text-sm">
      <span className="truncate text-slate-700">{label}</span>
      <span className="tabular-nums text-slate-900"><strong>{value.toLocaleString()}</strong> <span className="text-xs text-slate-500">{formatShare(value, total)}</span></span>
      <div className="col-span-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: color }} /></div>
    </div>
  );
}

function ProgressRow({ label, reviewed, remaining, total }: { label: string; reviewed: number; remaining: number; total: number }): JSX.Element {
  const width = total > 0 ? (reviewed / total) * 100 : 0;
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">{label}</span>
        <span className="text-xs text-slate-500">{formatShare(reviewed, total)} reviewed</span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${width}%` }} /></div>
      <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div><dt className="text-slate-500">Reviewed</dt><dd className="text-base font-semibold tabular-nums text-slate-900">{reviewed.toLocaleString()}</dd></div>
        <div><dt className="text-slate-500">Remaining</dt><dd className="text-base font-semibold tabular-nums text-slate-900">{remaining.toLocaleString()}</dd></div>
        <div><dt className="text-slate-500">Total</dt><dd className="text-base font-semibold tabular-nums text-slate-900">{total.toLocaleString()}</dd></div>
      </dl>
    </div>
  );
}

function KeyValueList({ items }: { items: Array<[string, ReactNode]> }): JSX.Element {
  return (
    <dl className="divide-y divide-slate-100 text-sm">
      {items.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3 py-2">
          <dt className="text-slate-600">{label}</dt>
          <dd className="font-semibold tabular-nums text-slate-900">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function LoadingPanel({ label }: { label: string }): JSX.Element {
  return <section className="surface-card p-6 text-sm text-slate-500" role="status">{label}</section>;
}

export default function AnalysisWorkspace(): JSX.Element {
  const { token, activeContext } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('analysisView');
  const view: AnalysisView = ANALYSIS_VIEWS.some((item) => item.id === requestedView) ? (requestedView as AnalysisView) : 'identity';
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [quality, setQuality] = useState<QualitySummary | null>(null);
  const [qualityState, setQualityState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [sourceStatus, setSourceStatus] = useState<Record<string, SourceStatusRecord> | null>(null);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [reasonFilter, setReasonFilter] = useState('ALL');
  const [sourceFilter, setSourceFilter] = useState('ALL');
  const [decisionFilter, setDecisionFilter] = useState('ALL');
  const [flags, setFlags] = useState({ multiSource: false, multipleEmails: false, multiplePhones: false, multipleAssociates: false });
  const [search, setSearch] = useState('');
  const [queueLimit, setQueueLimit] = useState(QUEUE_PAGE);
  const [pendingDecision, setPendingDecision] = useState('KEEP_REVIEW_REQUIRED');
  const [savingDecision, setSavingDecision] = useState(false);
  const [decisionError, setDecisionError] = useState<string | null>(null);

  const setView = (next: AnalysisView): void => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set('analysisView', next);
      return params;
    }, { replace: true });
  };

  useEffect(() => {
    if (!token || !activeContext?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void apiFetch('/api/leads-contacts/identity-analysis/summary', { headers: { Authorization: `Bearer ${token}`, 'X-Active-Context': activeContext.id } })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error ?? `Identity analysis failed (${response.status})`);
        if (!cancelled) setAnalysis(body as AnalysisResponse);
      })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Identity analysis failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeContext?.id, token]);

  useEffect(() => {
    if (!token || !activeContext?.id) return;
    let cancelled = false;
    setQualityState('loading');
    const headers = { Authorization: `Bearer ${token}`, 'X-Active-Context': activeContext.id };
    void apiFetch('/api/leads-contacts/quality-analysis/summary', { headers })
      .then(async (response) => {
        if (!response.ok) throw new Error('unavailable');
        const body = await response.json().catch(() => ({})) as QualityApiResponse;
        if (cancelled) return;
        if (body.summary) { setQuality(mergeQualitySummary(body.summary)); setQualityState('ready'); } else setQualityState('unavailable');
      })
      .catch(() => { if (!cancelled) setQualityState('unavailable'); });
    void apiFetch('/api/leads-contacts/integrations/status', { headers })
      .then(async (response) => (response.ok ? (await response.json().catch(() => null)) as Record<string, SourceStatusRecord> | null : null))
      .then((body) => { if (!cancelled) setSourceStatus(body); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [activeContext?.id, token]);

  const summary = analysis?.summary ?? {};
  const reviewCandidates = useMemo(() => (Array.isArray(analysis?.review) ? analysis.review : []), [analysis]);
  const candidateRows = Array.isArray(analysis?.candidates) ? analysis.candidates : [];
  const autoSample = Array.isArray(analysis?.autoSample) ? analysis.autoSample : [];
  const riskReport = analysis?.riskReport ?? { totalAutoMatch: 0, riskCounts: {}, reasonCounts: {}, genericSharedEmailCandidates: 0 };
  const decisions = analysis?.decisions ?? {};
  const reviewRequiredTotal = num(summary.reviewRequiredContacts);
  const decisionProjection = analysis?.decisionProjection ?? { reviewed: 0, remaining: reviewRequiredTotal, approvedCanonical: 0, splitRequired: 0, identifiersExcluded: 0, autoMatchRejected: 0, needsInvestigation: 0, stillReviewRequired: reviewRequiredTotal, projectedCanonicalContacts: 0, projectedIdentifiers: 0, projectedEnquiryEvents: 0 };
  const mediumRiskTotal = num(riskReport.riskCounts?.MEDIUM);
  const progress = summarizeReviewProgress(reviewCandidates.map((candidate) => candidate.candidateId), decisions, mediumRiskTotal);
  const reviewReasonCounts = reviewCandidates.reduce<Record<string, number>>((counts, candidate) => { for (const reason of candidate.conflictReasons ?? []) counts[reason] = (counts[reason] ?? 0) + 1; return counts; }, {});

  const filteredReview = reviewCandidates.filter((candidate) => {
    if (reasonFilter !== 'ALL' && !candidate.conflictReasons.includes(reasonFilter)) return false;
    if (sourceFilter !== 'ALL' && !candidate.sources.includes(sourceFilter)) return false;
    if (decisionFilter === 'OPEN' && decisions[candidate.candidateId]) return false;
    if (decisionFilter === 'REVIEWED' && !decisions[candidate.candidateId]) return false;
    if (flags.multiSource && candidate.sourceCount < 2) return false;
    if (flags.multipleEmails && candidate.identifiers.emails.length < 2) return false;
    if (flags.multiplePhones && candidate.identifiers.phones.length < 2) return false;
    if (flags.multipleAssociates && candidate.associatedAssociates.length < 2) return false;
    if (search.trim()) {
      const haystack = [candidate.candidateId, candidate.proposedDisplayName, ...candidate.conflictReasons, ...candidate.sources, ...candidate.identifiers.emails, ...candidate.identifiers.phones].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(search.trim().toLowerCase())) return false;
    }
    return true;
  });

  const openCandidate = (candidate: Candidate): void => {
    setSelected(candidate);
    setDecisionError(null);
    setPendingDecision(decisions[candidate.candidateId]?.decision ?? (candidate.matchClassification === 'AUTO_MATCH' ? 'APPROVE_AUTO_MATCH' : 'KEEP_REVIEW_REQUIRED'));
  };

  const saveDecision = async (): Promise<void> => {
    if (!import.meta.env.DEV || !selected || !token || !activeContext?.id) return;
    setSavingDecision(true);
    setDecisionError(null);
    try {
      const response = await apiFetch('/api/leads-contacts/identity-analysis/decisions', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'X-Active-Context': activeContext.id, 'Content-Type': 'application/json' }, body: JSON.stringify({ candidateId: selected.candidateId, decision: pendingDecision, reasonCodes: selected.conflictReasons, notes: 'Local adjudication pilot decision' }) });
      if (!response.ok) throw new Error('Decision could not be saved locally.');
      const body = await response.json() as { decision: IdentityDecision };
      setAnalysis((current) => current ? { ...current, decisions: { ...(current.decisions ?? {}), [selected.candidateId]: body.decision } } : current);
    } catch (reason) {
      setDecisionError(reason instanceof Error ? reason.message : 'Decision could not be saved locally.');
    } finally { setSavingDecision(false); }
  };

  const proposed = num(summary.proposedUniqueContacts);
  const autoMatch = num(summary.autoMatchContacts);
  const totalEvents = num(summary.totalLegacyEnquiryEvents);
  const unmatchedEvents = num(summary.unmatchedEvents);

  return (
    <section className="space-y-4" aria-label="Regional analysis workspace">
      <header className="surface-card p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Regional Admin · Internal intelligence</p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">Lead &amp; Contact Analysis</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">Read-only analysis of stored portal lead events. Lead delivery never waits for anything in this workspace; review here improves canonical data quality, reporting, and matching rules.</p>
          </div>
          <div className="flex flex-wrap gap-2"><StatusBadge label="READ-ONLY" tone="info" /><StatusBadge label="No canonical writes" tone="good" /></div>
        </div>
        <div className="mt-4">
          <StatStrip>
            <Stat label="Lead events analysed" value={loading ? '…' : totalEvents.toLocaleString()} hint="Stored legacy portal events" />
            <Stat label="Proposed contacts" value={loading ? '…' : proposed.toLocaleString()} hint="Distinct people from identifiers" />
            <Stat label="Auto-match rate" value={loading ? '…' : formatShare(autoMatch, proposed)} hint={`${autoMatch.toLocaleString()} resolved automatically`} />
            <Stat label="Data quality review" value={loading ? '…' : progress.review.remaining.toLocaleString()} hint="Open identities · non-blocking" />
          </StatStrip>
        </div>
        <nav className="mt-4 overflow-x-auto" aria-label="Analysis sections">
          <div className="inline-flex min-w-max gap-1 rounded-lg bg-slate-100 p-1" role="tablist">
            {ANALYSIS_VIEWS.map((item) => (
              <button key={item.id} type="button" role="tab" aria-selected={view === item.id} onClick={() => setView(item.id)} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${view === item.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>{item.label}</button>
            ))}
          </div>
        </nav>
      </header>

      {view === 'identity' ? (
        loading ? <LoadingPanel label="Loading identity analysis…" />
          : error ? <section className="surface-card p-5"><EmptyState title="Identity analysis unavailable" detail={error} /></section>
            : !analysis ? <section className="surface-card p-5"><EmptyState title="No identity analysis" detail="No analysis response was returned." /></section>
              : (
                <div className="space-y-4">
                  <Panel title="Identity resolution" description="Deterministic clustering of stored lead events by verified email and phone identifiers. Names never create a match on their own.">
                    <StatStrip>
                      <Stat label="Proposed contacts" value={proposed.toLocaleString()} />
                      <Stat label="Auto-match" value={autoMatch.toLocaleString()} hint={`${formatShare(autoMatch, proposed)} of proposed`} />
                      <Stat label="Review required" value={reviewRequiredTotal.toLocaleString()} hint={`${formatShare(reviewRequiredTotal, proposed)} of proposed`} />
                      <Stat label="Unmatched events" value={unmatchedEvents.toLocaleString()} hint="No usable identifier" />
                    </StatStrip>
                    <div className="mt-4 grid gap-x-8 sm:grid-cols-2 xl:grid-cols-3">
                      <KeyValueList items={[['Multi-source contacts', num(summary.multiSourceContacts).toLocaleString()], ['Multiple associates', num(summary.multipleAssociateContacts).toLocaleString()]]} />
                      <KeyValueList items={[['Multiple emails', num(summary.multipleEmailContacts).toLocaleString()], ['Multiple phones', num(summary.multiplePhoneContacts).toLocaleString()]]} />
                      <KeyValueList items={[['DNC-linked contacts', num(summary.dncLinkedContacts).toLocaleString()], ['Generic shared email', num(riskReport.genericSharedEmailCandidates).toLocaleString()]]} />
                    </div>
                  </Panel>

                  <div className="grid gap-4 xl:grid-cols-2">
                    <Panel title="Identity risk" description="Risk grading of automatically matched contacts.">
                      <div className="space-y-3">
                        {(['LOW', 'MEDIUM', 'HIGH'] as const).map((risk) => <BarRow key={risk} label={`${humanize(risk)} risk`} value={num(riskReport.riskCounts?.[risk])} total={num(riskReport.totalAutoMatch) || autoMatch} color={risk === 'LOW' ? '#059669' : risk === 'MEDIUM' ? '#f59e0b' : '#e11d48'} />)}
                      </div>
                    </Panel>
                    <Panel title="Review reasons" description="Why identities were held for data quality review.">
                      {Object.keys(reviewReasonCounts).length ? (
                        <div className="space-y-3">
                          {Object.entries(reviewReasonCounts).sort((a, b) => b[1] - a[1]).map(([reason, count]) => <BarRow key={reason} label={humanize(reason)} value={count} total={reviewCandidates.length} color="#0f172a" />)}
                        </div>
                      ) : <EmptyState title="No review reasons" detail="No identity conflicts were found in the current scope." />}
                    </Panel>
                  </div>

                  <Panel title="Data Quality Review progress" description="Review is informational and non-blocking. Every valid lead continues through the existing delivery path." aside={<StatusBadge label={import.meta.env.DEV ? 'Local decisions only' : 'READ-ONLY'} tone="info" />}>
                    <div className="grid gap-3 md:grid-cols-2">
                      <ProgressRow label="Review-required identities" reviewed={progress.review.reviewed} remaining={progress.review.remaining} total={progress.review.total} />
                      <ProgressRow label="Medium-risk auto-matches" reviewed={progress.mediumRisk.reviewed} remaining={progress.mediumRisk.remaining} total={progress.mediumRisk.total} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <StatusBadge label={`Confirmed ${num(decisionProjection.approvedCanonical)}`} tone="good" />
                      <StatusBadge label={`Split ${num(decisionProjection.splitRequired)}`} tone="warning" />
                      <StatusBadge label={`Identifiers excluded ${num(decisionProjection.identifiersExcluded)}`} tone="warning" />
                      <StatusBadge label={`Auto-match rejected ${num(decisionProjection.autoMatchRejected)}`} tone="warning" />
                      <StatusBadge label={`Investigate ${num(decisionProjection.needsInvestigation)}`} tone="info" />
                    </div>
                  </Panel>

                  <section className="surface-card overflow-hidden">
                    <div className="border-b border-slate-200 p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-slate-900">Data Quality Review queue</h3>
                          <p className="mt-1 text-sm text-slate-500">Showing {Math.min(queueLimit, filteredReview.length).toLocaleString()} of {filteredReview.length.toLocaleString()} matching identities ({reviewCandidates.length.toLocaleString()} total).</p>
                        </div>
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))]">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Search<input value={search} onChange={(event) => { setSearch(event.target.value); setQueueLimit(QUEUE_PAGE); }} placeholder="Name, candidate, identifier" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800" /></label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reason<select value={reasonFilter} onChange={(event) => { setReasonFilter(event.target.value); setQueueLimit(QUEUE_PAGE); }} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800"><option value="ALL">All reasons</option>{REVIEW_REASONS.map((reason) => <option key={reason} value={reason}>{humanize(reason)}</option>)}</select></label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source<select value={sourceFilter} onChange={(event) => { setSourceFilter(event.target.value); setQueueLimit(QUEUE_PAGE); }} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800"><option value="ALL">All sources</option>{SOURCES.map((source) => <option key={source.id} value={source.id}>{source.label}</option>)}</select></label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status<select value={decisionFilter} onChange={(event) => { setDecisionFilter(event.target.value); setQueueLimit(QUEUE_PAGE); }} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800"><option value="ALL">All</option><option value="OPEN">Not reviewed</option><option value="REVIEWED">Reviewed</option></select></label>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {([['multiSource', 'Multi-source'], ['multipleEmails', 'Multiple emails'], ['multiplePhones', 'Multiple phones'], ['multipleAssociates', 'Multiple associates']] as const).map(([key, label]) => (
                          <button key={key} type="button" aria-pressed={flags[key]} onClick={() => { setFlags((current) => ({ ...current, [key]: !current[key] })); setQueueLimit(QUEUE_PAGE); }} className={`rounded-full border px-3 py-1 text-xs font-semibold ${flags[key] ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
                        ))}
                      </div>
                    </div>
                    {filteredReview.length ? (
                      <>
                        <ul className="divide-y divide-slate-100">
                          {filteredReview.slice(0, queueLimit).map((candidate) => (
                            <li key={candidate.candidateId}>
                              <button type="button" onClick={() => openCandidate(candidate)} className="grid w-full gap-2 px-5 py-3 text-left hover:bg-slate-50 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                                <span className="min-w-0">
                                  <span className="block truncate font-semibold text-slate-900">{candidate.proposedDisplayName ?? 'Name not recorded'}</span>
                                  <span className="mt-0.5 block text-xs text-slate-500">{candidate.conflictReasons.map(humanize).join(', ') || 'Review required'} · {candidate.sources.map((id) => SOURCES.find((source) => source.id === id)?.label ?? id).join(', ')} · {candidate.enquiryCount} enquiries · {candidate.associatedAssociates.length} associates</span>
                                  {candidate.classificationChanged ? <span className="mt-1 block text-[11px] font-semibold text-rose-700">{humanize(candidate.previousClassification)} → {humanize(candidate.matchClassification)} · {candidate.downgradeReasons.map(humanize).join(', ')}</span> : null}
                                </span>
                                <StatusBadge label={decisions[candidate.candidateId] ? decisionLabel(decisions[candidate.candidateId].decision) : 'Not reviewed'} tone={decisions[candidate.candidateId] ? 'good' : 'neutral'} />
                              </button>
                            </li>
                          ))}
                        </ul>
                        {filteredReview.length > queueLimit ? <div className="border-t border-slate-200 p-3 text-center"><button type="button" onClick={() => setQueueLimit((value) => value + QUEUE_PAGE)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">Show {Math.min(QUEUE_PAGE, filteredReview.length - queueLimit)} more</button></div> : null}
                      </>
                    ) : <div className="p-6"><EmptyState title="No identities match these filters" detail="Adjust the search or filters to see more of the review queue." /></div>}
                  </section>

                  <details className="surface-card group overflow-hidden">
                    <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 p-5">
                      <span><span className="block text-base font-semibold text-slate-900">Auto-match sample</span><span className="block text-sm text-slate-500">Deterministic sample of {autoSample.length} automatically matched identities for spot checks.</span></span>
                      <span className="text-xs font-semibold text-sky-700 group-open:hidden">Show sample</span><span className="hidden text-xs font-semibold text-sky-700 group-open:inline">Hide sample</span>
                    </summary>
                    <div className="overflow-x-auto border-t border-slate-200">
                      <table className="w-full min-w-[720px] text-left text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-2">Contact</th><th className="px-4 py-2">Risk</th><th className="px-4 py-2">Sources</th><th className="px-4 py-2 text-right">Enquiries</th><th className="px-4 py-2"><span className="sr-only">Action</span></th></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {autoSample.map((candidate) => (
                            <tr key={candidate.candidateId}>
                              <td className="px-4 py-2.5 font-semibold text-slate-900">{candidate.proposedDisplayName ?? 'Name not recorded'}</td>
                              <td className="px-4 py-2.5"><StatusBadge label={humanize(candidate.risk)} tone={statusTone(candidate.risk)} /></td>
                              <td className="px-4 py-2.5 text-xs text-slate-600">{candidate.sources.map((id) => SOURCES.find((source) => source.id === id)?.label ?? id).join(', ')}</td>
                              <td className="px-4 py-2.5 text-right tabular-nums">{candidate.enquiryCount}</td>
                              <td className="px-4 py-2.5 text-right"><button type="button" onClick={() => openCandidate(candidate)} className="rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Inspect</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              )
      ) : null}

      {view === 'quality' ? <LeadQualityView quality={quality} state={qualityState} /> : null}
      {view === 'sources' ? <SourceHealthView quality={quality} qualityState={qualityState} sourceStatus={sourceStatus} /> : null}
      {view === 'readiness' ? (
        loading ? <LoadingPanel label="Loading canonical readiness…" />
          : error ? <section className="surface-card p-5"><EmptyState title="Readiness unavailable" detail={error} /></section>
            : <ReadinessView proposed={proposed} projectedLinks={Math.max(0, totalEvents - unmatchedEvents)} unresolved={progress.review.remaining} reviewed={progress.review.reviewed + progress.mediumRisk.reviewed} reviewTotal={progress.review.total + progress.mediumRisk.total} adjudicatedContacts={num(decisionProjection.projectedCanonicalContacts)} sourceStatus={sourceStatus} candidateCount={candidateRows.length} />
      ) : null}

      {selected ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30" role="presentation" onMouseDown={() => setSelected(null)}>
          <aside className="h-full w-full max-w-3xl overflow-y-auto bg-white p-5 shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="identity-candidate-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-4">
              <div className="min-w-0">
                <div className="flex flex-wrap gap-2"><StatusBadge label={humanize(selected.matchClassification)} tone={statusTone(selected.matchClassification)} /><StatusBadge label={`${humanize(selected.risk)} risk`} tone={statusTone(selected.risk)} /></div>
                <h3 id="identity-candidate-title" className="mt-2 break-words text-xl font-semibold text-slate-900">{selected.proposedDisplayName ?? 'Name not recorded'}</h3>
                <p className="mt-1 break-all text-xs text-slate-500">{selected.candidateId}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Close</button>
            </div>
            <div className="mt-5 space-y-5">
              {import.meta.env.DEV ? <DrawerSection title="Data quality decision">
                <p className="mt-1 text-xs text-slate-500">Stored in the local decision file only. Lead delivery and sync are never affected.</p>
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <select aria-label="Data quality decision" value={pendingDecision} onChange={(event) => setPendingDecision(event.target.value)} className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800">{DECISION_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select>
                  <button type="button" disabled={savingDecision} onClick={() => void saveDecision()} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{savingDecision ? 'Saving…' : 'Save decision'}</button>
                  {decisions[selected.candidateId] ? <span className="text-xs text-slate-500">Current: {decisionLabel(decisions[selected.candidateId].decision)}</span> : null}
                </div>
                {decisionError ? <p className="mt-2 text-xs text-rose-700">{decisionError}</p> : null}
              </DrawerSection> : null}
              <DrawerSection title="Match and conflict reasons"><div className="flex flex-wrap gap-2">{[...selected.matchReasons, ...selected.conflictReasons].map((reason) => <StatusBadge key={reason} label={humanize(reason)} tone="warning" />)}{!selected.matchReasons.length && !selected.conflictReasons.length ? <span className="text-sm text-slate-500">None recorded</span> : null}</div></DrawerSection>
              <DrawerSection title="Observed names"><p className="break-words text-sm text-slate-700">{selected.observedNames.map((item) => `${item.name} (${item.frequency})`).join(', ') || 'No names recorded'}</p></DrawerSection>
              <DrawerSection title="Match graph"><div className="space-y-1 break-words text-xs text-slate-600">{selected.matchGraph.map((edge) => <div key={edge}>{edge}</div>)}</div></DrawerSection>
              <DrawerSection title="Associated agents"><div className="divide-y divide-slate-100 text-sm text-slate-700">{selected.associatedAssociates.map((agent) => <div key={`${agent.name}-${agent.kwuid}-${agent.marketCentre}`} className="break-words py-2 first:pt-0 last:pb-0">{agent.name ?? 'Unassigned'}{agent.kwuid ? ` · ${agent.kwuid}` : ''} · {agent.marketCentre ?? 'No Market Centre'} · {agent.enquiryCount} enquiries</div>)}</div></DrawerSection>
              <DrawerSection title="Enquiry events"><div className="divide-y divide-slate-100">{selected.enquiries.map((enquiry) => <div key={enquiry.id} className="py-3 text-sm first:pt-0 last:pb-0"><div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 font-semibold text-slate-800"><span>{SOURCES.find((source) => source.id === enquiry.source)?.label ?? enquiry.source} · {leadTypeLabel(enquiry.leadType)}</span><span className="text-xs font-normal text-slate-500">{dateLabel(enquiry.occurredAt ?? enquiry.receivedAt)}</span></div><div className="mt-1 break-words text-xs text-slate-600">{enquiry.listingReference ?? 'No listing'} · {enquiry.associateName ?? 'Unassigned'}{enquiry.associateKwuid ? ` · KWUID ${enquiry.associateKwuid}` : ''}</div><div className="mt-1 break-all text-[11px] text-slate-400">Source event ID: {enquiry.sourceEventId}</div></div>)}</div></DrawerSection>
            </div>
          </aside>
        </div>
      ) : null}
    </section>
  );
}

function LeadQualityView({ quality, state }: { quality: QualitySummary | null; state: 'loading' | 'ready' | 'unavailable' }): JSX.Element {
  if (state === 'loading') return <LoadingPanel label="Loading lead quality analysis…" />;
  if (!quality) return <section className="surface-card p-5"><EmptyState title="Lead quality unavailable" detail="The read-only quality analysis did not return a summary." /></section>;
  const total = quality.totalEvents;
  const counts = quality.byClassification;
  const sourceRows = SOURCES.map((source) => {
    const bySource = quality.bySource[source.id] ?? {};
    const sourceTotal = Object.values(bySource).reduce((sum, value) => sum + num(value), 0);
    return { ...source, counts: bySource, total: sourceTotal };
  });
  return (
    <div className="space-y-4">
      <Panel title="Classification mix" description="Event authenticity is assessed separately from identity confidence. No lead is hidden, delayed, or changed." aside={<span className="text-sm text-slate-500"><strong className="tabular-nums text-slate-900">{total.toLocaleString()}</strong> events</span>}>
        <div className="flex h-3 overflow-hidden rounded-full bg-slate-100" role="img" aria-label="Lead quality classification mix">
          {QUALITY_CLASSES.map((item) => num(counts[item.id]) > 0 ? <div key={item.id} title={`${item.label}: ${num(counts[item.id])}`} style={{ width: `${(num(counts[item.id]) / Math.max(1, total)) * 100}%`, backgroundColor: item.color }} /> : null)}
        </div>
        <div className="mt-4 grid gap-x-8 gap-y-1 sm:grid-cols-2 xl:grid-cols-4">
          {QUALITY_CLASSES.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-2 border-b border-slate-100 py-2 text-sm">
              <span className="inline-flex min-w-0 items-center gap-2 text-slate-700"><span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} aria-hidden="true" /><span className="truncate">{item.label}</span></span>
              <span className="whitespace-nowrap tabular-nums"><strong className="text-slate-900">{num(counts[item.id]).toLocaleString()}</strong> <span className="text-xs text-slate-500">{formatShare(num(counts[item.id]), total)}</span></span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Quality by source" description="Classification counts per portal, with rates relative to each source's own volume.">
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-y border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-5 py-2">Source</th><th className="px-3 py-2 text-right">Events</th>{QUALITY_CLASSES.map((item) => <th key={item.id} className="px-3 py-2 text-right">{item.label}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sourceRows.map((row) => (
                <tr key={row.id}>
                  <td className="px-5 py-2.5"><span className="inline-flex items-center gap-2 font-semibold text-slate-800"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: row.color }} aria-hidden="true" />{row.label}</span></td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{row.total.toLocaleString()}</td>
                  {QUALITY_CLASSES.map((item) => <td key={item.id} className="px-3 py-2.5 text-right tabular-nums"><div>{num(row.counts[item.id]).toLocaleString()}</div><div className="text-[11px] text-slate-400">{formatShare(num(row.counts[item.id]), row.total)}</div></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title="High-volume analysis" description="Identifiers submitting many enquiries per day. Genuine active buyers are preserved as normal.">
          <KeyValueList items={[
            ['More than 5 per day', quality.highVolumeOver5PerDay.toLocaleString()],
            ['More than 10 per day', quality.highVolumeOver10PerDay.toLocaleString()],
            ['More than 20 per day', quality.highVolumeOver20PerDay.toLocaleString()],
            ['High-volume, classified normal', quality.highVolumeNormalEvents.toLocaleString()],
            ['Suspicious high-volume', quality.suspiciousHighVolumeEvents.toLocaleString()],
          ]} />
        </Panel>
        <Panel title="Repeated-message analysis" description="Duplicate and templated submissions across portals.">
          <KeyValueList items={[
            ['Exact duplicates', quality.exactDuplicates.toLocaleString()],
            ['Near duplicates', quality.nearDuplicates.toLocaleString()],
            ['Repeated messages', quality.repeatedMessages.toLocaleString()],
            ['Repeated interest preserved', quality.repeatedInterestPreserved.toLocaleString()],
            ['Portal-template messages', quality.portalTemplateMessages.toLocaleString()],
            ['Portal-template events', quality.portalTemplateEvents.toLocaleString()],
          ]} />
        </Panel>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel title="Internal and test signals" description="Events using associate or test identifiers.">
          <KeyValueList items={[
            ['Associate identifiers', quality.associateIdentifierEvents.toLocaleString()],
            ['Generic emails', quality.genericEmailEvents.toLocaleString()],
            ['Example / test domains', quality.exampleTestDomainEvents.toLocaleString()],
          ]} />
        </Panel>
        <Panel title="Medium-risk identities" description="Quality overlay for medium-risk auto-matches.">
          <KeyValueList items={[
            ['Candidates', quality.mediumRiskCandidates.toLocaleString()],
            ['Normal events only', quality.mediumRiskNormalOnly.toLocaleString()],
            ['With duplicates', quality.mediumRiskWithDuplicates.toLocaleString()],
            ['With internal / test', quality.mediumRiskWithInternalTest.toLocaleString()],
            ['With spam / malformed', quality.mediumRiskWithSpamOrMalformed.toLocaleString()],
          ]} />
        </Panel>
        <Panel title="Review-required identities" description="Quality overlay for identities in data quality review.">
          <KeyValueList items={[
            ['Candidates', quality.reviewCandidates.toLocaleString()],
            ['Normal events only', quality.reviewNormalOnly.toLocaleString()],
            ['With duplicates', quality.reviewWithDuplicates.toLocaleString()],
            ['With internal / test', quality.reviewWithInternalTest.toLocaleString()],
            ['With spam / malformed', quality.reviewWithSpamOrMalformed.toLocaleString()],
          ]} />
        </Panel>
      </div>
    </div>
  );
}

function SourceHealthView({ quality, qualityState, sourceStatus }: { quality: QualitySummary | null; qualityState: 'loading' | 'ready' | 'unavailable'; sourceStatus: Record<string, SourceStatusRecord> | null }): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        {SOURCES.map((source) => {
          const status = sourceStatus?.[source.statusKey];
          const counts = quality?.bySource[source.id] ?? {};
          const analysed = Object.values(counts).reduce((sum, value) => sum + num(value), 0);
          const rate = (key: string): string => (quality ? formatShare(num(counts[key]), analysed) : qualityState === 'loading' ? '…' : '—');
          return (
            <section key={source.id} className="surface-card p-5">
              <div className="flex items-start justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-base font-semibold text-slate-900"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: source.color }} aria-hidden="true" />{source.label}</span>
                <StatusBadge label={status?.state ? humanize(status.state) : sourceStatus ? 'Unknown' : 'Checking…'} tone={status?.state === 'AVAILABLE' ? 'good' : 'neutral'} />
              </div>
              <div className="mt-4 text-3xl font-semibold tabular-nums text-slate-900">{status?.total !== undefined ? num(status.total).toLocaleString() : '—'}</div>
              <div className="text-xs text-slate-500">Stored leads</div>
              <KeyValueList items={[
                ['Latest lead', <span key="latest" className="font-medium">{dateLabel(status?.lastLeadAt)}</span>],
                ['Duplicate rate', rate('LIKELY_DUPLICATE')],
                ['Template-message rate', rate('PORTAL_TEMPLATE_NORMAL')],
                ['Internal / test events', quality ? num(counts.INTERNAL_TEST).toLocaleString() : '—'],
                ['Canonical ingestion', status?.canonicalIngestionEnabled ? 'Enabled' : 'Disabled'],
              ]} />
            </section>
          );
        })}
      </div>
      <p className="px-1 text-xs text-slate-500">Stored totals come from the existing portal lead tables. Rates are calculated from the read-only quality analysis of stored events in your scope.</p>
    </div>
  );
}

function ReadinessView({ proposed, projectedLinks, unresolved, reviewed, reviewTotal, adjudicatedContacts, sourceStatus, candidateCount }: { proposed: number; projectedLinks: number; unresolved: number; reviewed: number; reviewTotal: number; adjudicatedContacts: number; sourceStatus: Record<string, SourceStatusRecord> | null; candidateCount: number }): JSX.Element {
  const ingestionEnabled = SOURCES.some((source) => sourceStatus?.[source.statusKey]?.canonicalIngestionEnabled);
  const flow = ['Lead arrives from a portal or KWHomes', 'Identity is normalised (email, mobile)', 'Canonical contact is resolved or created', 'Enquiry is recorded against the contact', 'Associate is resolved from the listing or source', 'DNC and marketing eligibility are evaluated', 'Lead syncs automatically to the associate\'s Command account (when connected)'];
  return (
    <div className="space-y-4">
      <Panel title="Readiness status" description="Canonical backfill and live ingestion require a separate, reviewed approval. This workspace never writes canonical data." aside={<StatusBadge label="Analysis only" tone="info" />}>
        <StatStrip>
          <Stat label="Proposed canonical contacts" value={proposed.toLocaleString()} />
          <Stat label="Projected enquiry links" value={projectedLinks.toLocaleString()} hint="Events with a usable identifier" />
          <Stat label="Unresolved identities" value={unresolved.toLocaleString()} hint="Resolved with best identity; non-blocking" />
          <Stat label="Review progress" value={formatShare(reviewed, reviewTotal)} hint={`${reviewed.toLocaleString()} of ${reviewTotal.toLocaleString()} reviewed`} />
        </StatStrip>
        <div className="mt-4 grid gap-x-8 md:grid-cols-2">
          <KeyValueList items={[
            ['Canonical backfill', <StatusBadge key="backfill" label="Not started · approval required" />],
            ['Live canonical ingestion', <StatusBadge key="ingestion" label={ingestionEnabled ? 'Enabled' : 'Disabled'} tone={ingestionEnabled ? 'warning' : 'neutral'} />],
            ['Command sync', <StatusBadge key="command" label="Paused" />],
          ]} />
          <KeyValueList items={[
            ['Canonical writes from this workspace', <StatusBadge key="writes" label="None" tone="good" />],
            ['Locally adjudicated contacts', adjudicatedContacts.toLocaleString()],
            ['Candidates in analysis sample', candidateCount.toLocaleString()],
          ]} />
        </div>
      </Panel>
      <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <Panel title="Automatic lead flow" description="Leads must never wait for human approval before delivery or sync.">
          <ol className="space-y-2">
            {flow.map((step, index) => (
              <li key={step} className="flex items-start gap-3 text-sm text-slate-700"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">{index + 1}</span><span className="pt-0.5">{step}</span></li>
            ))}
          </ol>
        </Panel>
        <Panel title="Operating principles">
          <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-slate-600">
            <li>Leads are never held for manual approval. Human review is for data quality, duplicate reconciliation, reporting, and rule improvement only.</li>
            <li>Leads link to canonical contacts automatically from identifiers. Normal users never attach leads to contacts by hand.</li>
            <li>When Command is connected, every valid lead assigned to an associate syncs automatically — including review-required identities, using the best resolved contact.</li>
            <li>Later reconciliation may merge or split contacts without delaying lead delivery.</li>
          </ul>
        </Panel>
      </div>
    </div>
  );
}
